import { currentLanguage } from '@/platform/i18n';

// 国データ（ISO 3166-1 alpha-2）。プレイヤー登録の国選択とランキングの国旗表示に使う。
// 国旗絵文字はコードから算出（各文字を Regional Indicator Symbol に変換）。

/** 国コード(2字) → 国旗絵文字。無効な入力は地球儀を返す。 */
export function flagEmoji(code: string | null | undefined): string {
  if (!code || !/^[A-Za-z]{2}$/.test(code)) return '🌐';
  const cc = code.toUpperCase();
  const A = 0x1f1e6;
  return String.fromCodePoint(A + (cc.charCodeAt(0) - 65), A + (cc.charCodeAt(1) - 65));
}

export interface Country {
  code: string; // ISO 3166-1 alpha-2
  name: string; // 日本語名（フォールバック。通常は Intl.DisplayNames が表示言語の名前を返す）
}

/**
 * 表示言語での国名。**`Intl.DisplayNames` に任せる**ので、言語を増やしても
 * 国名の対訳表を持たなくて済む（56件×言語を手で保守しない）。
 * 未対応環境・未知コードは下の日本語表を使う。
 */
let displayNames: { of(code: string): string | undefined } | null = null;
let displayNamesLang = '';

function localizedName(code: string, lang: string): string | undefined {
  if (typeof Intl === 'undefined' || typeof Intl.DisplayNames !== 'function') return undefined;
  try {
    if (!displayNames || displayNamesLang !== lang) {
      displayNames = new Intl.DisplayNames([lang], { type: 'region' });
      displayNamesLang = lang;
    }
    const v = displayNames.of(code.toUpperCase());
    // 未知のコードは入力をそのまま返す仕様なので、その場合は無効扱いにする。
    return v && v.toUpperCase() !== code.toUpperCase() ? v : undefined;
  } catch {
    return undefined;
  }
}

// 選択候補（主要国＋日本語話者に馴染みのある国）。日本を先頭に。
export const COUNTRIES: Country[] = [
  { code: 'JP', name: '日本' },
  { code: 'US', name: 'アメリカ' },
  { code: 'CN', name: '中国' },
  { code: 'TW', name: '台湾' },
  { code: 'KR', name: '韓国' },
  { code: 'HK', name: '香港' },
  { code: 'GB', name: 'イギリス' },
  { code: 'FR', name: 'フランス' },
  { code: 'DE', name: 'ドイツ' },
  { code: 'IT', name: 'イタリア' },
  { code: 'ES', name: 'スペイン' },
  { code: 'PT', name: 'ポルトガル' },
  { code: 'NL', name: 'オランダ' },
  { code: 'CH', name: 'スイス' },
  { code: 'SE', name: 'スウェーデン' },
  { code: 'NO', name: 'ノルウェー' },
  { code: 'FI', name: 'フィンランド' },
  { code: 'PL', name: 'ポーランド' },
  { code: 'RU', name: 'ロシア' },
  { code: 'UA', name: 'ウクライナ' },
  { code: 'TR', name: 'トルコ' },
  { code: 'CA', name: 'カナダ' },
  { code: 'MX', name: 'メキシコ' },
  { code: 'BR', name: 'ブラジル' },
  { code: 'AR', name: 'アルゼンチン' },
  { code: 'AU', name: 'オーストラリア' },
  { code: 'NZ', name: 'ニュージーランド' },
  { code: 'IN', name: 'インド' },
  { code: 'ID', name: 'インドネシア' },
  { code: 'TH', name: 'タイ' },
  { code: 'VN', name: 'ベトナム' },
  { code: 'PH', name: 'フィリピン' },
  { code: 'MY', name: 'マレーシア' },
  { code: 'SG', name: 'シンガポール' },
  { code: 'AE', name: 'アラブ首長国連邦' },
  { code: 'SA', name: 'サウジアラビア' },
  { code: 'EG', name: 'エジプト' },
  { code: 'ZA', name: '南アフリカ' },
];

/** 国コード → 表示言語での国名（未知はコードをそのまま返す）。 */
export function countryName(code: string | null | undefined): string {
  if (!code) return '';
  return (
    localizedName(code, currentLanguage()) ??
    COUNTRIES.find((c) => c.code === code.toUpperCase())?.name ??
    code.toUpperCase()
  );
}
